import { Box, Typography, useTheme } from '@mui/material';
import { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { getDefaultVisibility } from '@verantos-dev/core-annotation-tool';
import type { Annotation, AttributeValue } from '@verantos-dev/core-annotation-tool';
import { ErrorAlertCard } from '@verantos-dev/ui-annotation-tool';

interface SavedAnnotationCardProps {
  annotation: Annotation;
  deleteError?: string | null;
  onDismissDeleteError?: () => void;
}

export const SavedAnnotationCard = ({
  annotation,
  deleteError,
  onDismissDeleteError,
}: SavedAnnotationCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (deleteError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [deleteError]);

  const getAttributeValue = (name: string) => {
    const attr = annotation.attributes.find((a: AttributeValue) => a.name === name);
    return attr?.unknown ? undefined : attr?.value ?? undefined;
  };

  const filteredAttributes = annotation.attributes.filter(
    (attribute: AttributeValue) =>
      (attribute.unknown === true || (attribute.value !== null && attribute.value !== undefined)) &&
      getDefaultVisibility(attribute.name, getAttributeValue)
  );

  return (
    <>
      <Box
        id="saved-attribute-values-container"
        sx={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' }}
      >
        {filteredAttributes.map((attribute: AttributeValue, attributeIndex: number) => (
          <Box
            key={`${attribute.name}-${attributeIndex}`}
            sx={{ display: 'flex', gap: theme.spacing(2) }}
          >
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              {attribute.name}:
            </Typography>
            {typeof attribute.value === 'boolean' ? (
              <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: '700' }}>
                {formatMessage({
                  id: `workspaceCommon.savedAnnotationCard.attribute.${
                    attribute.value ? 'yes' : 'no'
                  }`,
                })}
              </Typography>
            ) : attribute.unknown ? (
              <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: '700' }}>
                {formatMessage({ id: `workspaceCommon.savedAnnotationCard.attribute.unknown` })}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: '700' }}>
                {attribute.value}
              </Typography>
            )}
            {attributeIndex !== filteredAttributes.length - 1 && (
              <Typography variant="body2">|</Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box ref={errorRef}>
        <ErrorAlertCard
          errorMessage={deleteError ?? ''}
          onDismiss={
            onDismissDeleteError ??
            (() => {
              /* noop */
            })
          }
        />
      </Box>
    </>
  );
};
